/**
 * Compare a given time stamp with the time zone offset (example: 2018-11-20T07:30+11:00[Australia/Sydney]) with the local time
 * Return true if the given time if after the local time and false otherwise.
 * @param aedtTime: AEDT time stamp
 */

interface CrisisBannerContent {
  start: string;
  end: string;
  type: BannerType;
  msg: string;
  heading: string;
  link: string;
  linkText: string;
  enabled: boolean | string;
}
interface SecurityBannerContent {
  msgDefault: string;
  msgError: string;
  msgA: string;
  msgB: string;
  msgNB: string;
  enabled: boolean | string;
}
type BannerId = "crisisBanner" | "securitybanner";
type BannerType = "warning" | "info";
type BannerContentKeys =
  | keyof CrisisBannerContent
  | keyof SecurityBannerContent
  | "cachedAt";
interface BannerContent {
  status: string;
  messageType?: string;
  content: { [index in BannerContentKeys]?: string | boolean | number };
}

function isAfterLocalTime(aedtTime: string) {
  if (aedtTime) {
    const matchResult = aedtTime.match(/^(.*)\[.*\]$/);
    if (matchResult == null) {
      throw new Error("Invalid time");
    }
    const [, time] = matchResult;
    const aedtTimeDate = new Date(time);

    if (aedtTimeDate.getTime() > Date.now()) {
      return true;
    } else {
      return false;
    }
  } else {
    return undefined;
  }
}

function getCookie(name) {
  return document.cookie
    .split(";")
    .filter(cookieStr => cookieStr.includes(`${name}=`))[0];
}

function getCookieVal(name) {
  const cookie = getCookie(name);
  if (cookie) {
    return cookie.substring(cookie.indexOf("=") + 1);
  } else {
    return null;
  }
}

function getAccountId() {
  const myAccCH = window.ContextHub.getStore("originmyacc");
  const accountDetails = myAccCH
    ? myAccCH.getItem("originStore.accountDetails")
    : null;
  return accountDetails ? accountDetails.accountId : null;
}

async function loadContent(bannerIdentifier: BannerId) {
  const content = loadContentFromSessionStorage(bannerIdentifier);
  return content ? content : loadContentFromCms(bannerIdentifier);
}

function loadContentFromSessionStorage(key: BannerId) {
  const cacheTtlMilli = 15 * 60 * 1000;
  const sessionContentStr = sessionStorage.getItem(key);
  if (sessionContentStr === null) {
    return;
  }
  const sessionContentObj = JSON.parse(sessionContentStr);

  const cachedAt = sessionContentObj.content.cachedAt;
  if (Date.now() - cachedAt < cacheTtlMilli) {
    return sessionContentObj;
  } else {
    return null;
  }
}

async function loadContentFromCms(bannerIdentifier: BannerId) {
  try {
    const response = await fetchContent(
      getContentApiEndpoint(bannerIdentifier)
    );
    sessionStorage.setItem(
      bannerIdentifier,
      JSON.stringify(getContentObj(response, bannerIdentifier))
    );
    return getContentObj(response, bannerIdentifier);
  } catch (e) {
    console.log(e);
    return null;
  }
}

function getContentApiEndpoint(identifier: BannerId) {
  switch (identifier) {
    case "crisisBanner":
      return window.bannerConfig.cbContentApi;
    case "securitybanner":
      return window.bannerConfig.sbContentApi;
    default:
      return;
  }
}

async function doFetch(url, requestOptions) {
  const response = await fetch(url, requestOptions);
  if (!response.ok) {
    throw new Error("Response was not an HTTP 200");
  }
  return response.json();
}

async function fetchContent(url) {
  /**
   * setting cache to no-cache to force the browser to get content from the cms
   * server as cms content can change rapidly especially when authors edit and
   * publishes multiple times within a very short period.
   */
  return doFetch(url, {
    method: "GET",
    cache: "no-cache"
  });
}

function updateSessionStorage(key: BannerId, value) {
  const sessionStr = sessionStorage.getItem(key);
  const sessionObj = sessionStr ? JSON.parse(sessionStr) : {};
  const newSessionObj = { ...sessionObj, ...value };
  sessionStorage.setItem(key, JSON.stringify(newSessionObj));
}

async function getNotification() {
  const userToken = getCookieVal("user-token");
  if (!userToken) {
    throw new Error("User token cannot be found");
  }

  return doFetch(window.bannerConfig.notificationApi, {
    method: "GET",
    cache: "no-cache",
    headers: new Headers({
      "Content-Type": "application/json",
      Authorization: userToken
    })
  });
}

function transformSecurityBannerMsgKeys(content): SecurityBannerContent {
  return {
    msgDefault: content.breachMessage,
    msgError: content.errorMessage,
    msgA: content.breachMessageA,
    msgB: content.breachMessageB,
    msgNB: content.noBreachMessage,
    enabled: content.enabled === "true" ? true : false
  };
}

function transformCrisisBannerMsgKeys(content): CrisisBannerContent {
  return {
    start: content.crisisTime,
    end: content.crisisOverTime,
    type: content.crisisBannerType === "1" ? "warning" : "info",
    msg: content.crisisMessage,
    heading: content.crisisHeading,
    link: content.crisisLinkURL,
    linkText: content.crisisLinkText,
    enabled: content.crisisEnabled === "true" ? true : false
  };
}

function renameCmsContentObjKeys(cmsContentObj, id: BannerId) {
  if (id === "securitybanner") {
    return transformSecurityBannerMsgKeys(cmsContentObj);
  } else if (id === "crisisBanner") {
    return transformCrisisBannerMsgKeys(cmsContentObj);
  }
}

function getContentObj(response, key: BannerId): BannerContent {
  let contentObj: BannerContent;
  const bannerContent = response[key]
    ? renameCmsContentObjKeys(response[key], key)
    : null;

  if (bannerContent && bannerContent.enabled) {
    contentObj = {
      status: "open",
      ...(key === "securitybanner" ? { messageType: "msgDefault" } : {}),
      content: {
        ...bannerContent,
        cachedAt: new Date().getTime()
      }
    };
  } else {
    contentObj = {
      status: "disabled",
      content: { cachedAt: new Date().getTime() }
    };
  }
  return contentObj;
}

export {
  loadContent,
  getNotification,
  isAfterLocalTime,
  updateSessionStorage,
  getAccountId,
  getCookieVal
};
